import React, { useCallback, useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { COVER_AI_RUN_SIZE, canUseCoverAi, coverAiUpsellAlwaysShown } from 'fym-shared/src/music24/coverEditorPlans';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { releaseThemeMessages } from '../../../music24/promote/themes/releaseThemeMessages';
import { useCoverEditor } from '../../CoverEditorContext';
import { aiRunErrorMessages, coverEditorMessages } from '../../coverEditorMessages';
import { canStartCoverAiRun } from '../../states/firstOpen';
import { AiLocked } from './AiLocked';
import { AiMeter } from './AiMeter';
import { AiReferences } from './AiReferences';
import { AiResults } from './AiResults';
import { coverAiRuns } from './aiPictures';
import { RightsSheet } from './RightsSheet';
import { useCoverAiRun, type CoverReference } from './useCoverAiRun';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The longest request text (the API takes at most this many characters). */
export const COVER_INSTRUCTIONS_MAX = 600;

interface AiTabProps {
  onOpenBank: () => void;
}

/**
 * The AI tab of the Picture panel (design cover-editor b1, c1, c2): what the picture shows
 * (prefilled from the song, so "Make 4 pictures" works with no typing), up to 3 reference
 * pictures, the button, the pictures and the month count. The trial with its welcome run sees the
 * same form with "Make 4 pictures, on us" and no month count. A plan with no AI sees its pictures
 * from before and the lock card (on the Free plan the lock card is pinned above the panel).
 */
const AiTabComponent: React.FC<AiTabProps> = ({ onOpenBank }) => {
  const intl = useIntl();
  const instructionsId = useId();
  const { data, readOnly } = useCoverEditor();
  const { song, plan, quota, welcomeRun } = data;
  const isWelcome = welcomeRun?.available === true;
  const defaultText = useMemo(
    () =>
      intl.formatMessage(coverEditorMessages.aiDefaultInstructions, {
        title: song.title,
        feeling: intl.formatMessage(releaseThemeMessages[song.themeId].line),
      }),
    [intl, song.themeId, song.title]
  );
  const [instructions, setInstructions] = useState(defaultText);
  const [references, setReferences] = useState<CoverReference[]>([]);
  const ai = useCoverAiRun();
  const isWorking = useMemo(() => coverAiRuns(data.pictures)[0]?.isWorking === true, [data.pictures]);
  const change = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) =>
      setInstructions(event.target.value.slice(0, COVER_INSTRUCTIONS_MAX)),
    []
  );
  const generate = useCallback(() => ai.generate(instructions, references), [ai, instructions, references]);

  if (!canUseCoverAi(plan) && !isWelcome) {
    return (
      <>
        <AiResults />
        {!coverAiUpsellAlwaysShown(plan) && <AiLocked onOpenBank={onOpenBank} />}
      </>
    );
  }
  const canStart = canStartCoverAiRun(plan, quota, welcomeRun) && !isWorking && !readOnly;
  let buttonMessage = coverEditorMessages.aiGenerate;
  if (isWorking) {
    buttonMessage = coverEditorMessages.aiWorking;
  } else if (isWelcome) {
    buttonMessage = coverEditorMessages.aiWelcomeGenerate;
  }
  return (
    <>
      <label htmlFor={instructionsId} className={styles.column}>
        <span className={styles.eyebrow}>{intl.formatMessage(coverEditorMessages.aiQuestion)}</span>
        <textarea
          id={instructionsId}
          className={styles.textArea}
          rows={4}
          value={instructions}
          disabled={readOnly}
          onChange={change}
        />
      </label>
      <AiReferences references={references} onChange={setReferences} />
      <SMLButton stretch omitCap onClick={generate} isLoading={ai.isStarting} disabled={!canStart || ai.isStarting}>
        {intl.formatMessage(buttonMessage, { count: COVER_AI_RUN_SIZE })}
      </SMLButton>
      {ai.error !== null && (
        <span className={styles.errorText} role="alert">
          {intl.formatMessage(aiRunErrorMessages[ai.error])}
        </span>
      )}
      <span className={styles.hint}>{intl.formatMessage(coverEditorMessages.aiNoText)}</span>
      <AiResults />
      <span className={styles.grow} />
      {isWelcome ? (
        <span className={styles.hint}>
          {intl.formatMessage(coverEditorMessages.aiWelcomeHint, { count: welcomeRun?.pictures ?? COVER_AI_RUN_SIZE })}
        </span>
      ) : (
        <AiMeter />
      )}
      <RightsSheet
        isOpen={ai.isRightsOpen}
        artistName={song.artistName}
        isBusy={ai.isStarting}
        onConfirm={ai.confirmRights}
        onCancel={ai.cancelRights}
      />
    </>
  );
};

export const AiTab = TypedMemo(AiTabComponent);
